import type { DashboardResponse } from "@rbim/shared";

export type DashboardBarangay = DashboardResponse["barangays"][number];
export type DashboardPrimaryTotals = Pick<
  DashboardResponse["summary"],
  "totalPopulation" | "totalHouseholds" | "totalFamilies"
>;

/**
 * Always resolve a map selection against the latest dashboard payload.
 * This prevents the details/LIRA panels from retaining a stale object after
 * the dashboard refreshes or after the authorized Municipality changes.
 */
export function reconcileDashboardBarangaySelection(
  selected: DashboardBarangay | null,
  barangays: readonly DashboardBarangay[],
): DashboardBarangay | null {
  if (!selected) return null;
  return barangays.find((barangay) => barangay.barangayId === selected.barangayId) ?? null;
}

/**
 * Municipality dashboards show municipality-wide totals by default. Once a
 * Barangay is selected on the map, the three primary cards must immediately
 * represent that Barangay instead. Resetting the map restores the municipality
 * totals.
 */
export function resolveDashboardPrimaryTotals(
  summary: DashboardResponse["summary"] | undefined,
  selectedBarangay: DashboardBarangay | null,
): DashboardPrimaryTotals | null {
  if (selectedBarangay) {
    return {
      totalPopulation: selectedBarangay.population,
      totalHouseholds: selectedBarangay.households,
      totalFamilies: selectedBarangay.families,
    };
  }

  if (!summary) return null;

  return {
    totalPopulation: summary.totalPopulation,
    totalHouseholds: summary.totalHouseholds,
    totalFamilies: summary.totalFamilies,
  };
}
